import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/utils/api';
import MemberLayout from '@/components/MemberLayout';
import { trackEvent, USAGE_EVENTS } from '@/utils/usageTracker';
import { Building2, Search, MapPin, Phone, Mail, ChevronLeft, ChevronRight, Loader2, Eye } from 'lucide-react';

const MemberProviders = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [province, setProvince] = useState('all');
  const [town, setTown] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ provinces: [], towns: [] });
  const [visibleColumns, setVisibleColumns] = useState(['name', 'physical_address', 'location', 'phone']);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '20' });
      if (search.trim()) params.append('search', search.trim());
      if (province !== 'all') params.append('province', province);
      if (town !== 'all') params.append('town', town);
      const res = await apiClient.get(`/directory/providers?${params.toString()}`);
      setProviders(res.data.providers || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Failed to load providers:', e);
    } finally {
      setLoading(false);
    }
  }, [page, search, province, town]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    trackEvent(USAGE_EVENTS.VIEW_PROVIDERS);
    apiClient.get('/directory/providers/filters').then(r => setFilters(r.data)).catch(() => {});
    apiClient.get('/directory/providers/settings').then(r => setVisibleColumns(r.data.visible_columns || [])).catch(() => {});
  }, []);

  const col = (c) => visibleColumns.includes(c);

  return (
    <MemberLayout>
      <div className="space-y-6" data-testid="member-providers">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6" /> Insurance Provider Directory
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Browse and search approved insurance providers.</p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search provider name..."
                  value={search}
                  onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                  data-testid="provider-search-input"
                />
              </div>
              <Select value={province} onValueChange={(v) => { setPage(1); setProvince(v); }}>
                <SelectTrigger data-testid="provider-province-filter"><SelectValue placeholder="All Provinces" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Provinces</SelectItem>
                  {filters.provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={town} onValueChange={(v) => { setPage(1); setTown(v); }}>
                <SelectTrigger data-testid="provider-town-filter"><SelectValue placeholder="All Towns" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Towns</SelectItem>
                  {filters.towns.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
        ) : providers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No providers found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {providers.map((p) => (
              <Card key={p.provider_id} className="hover:shadow-md transition-shadow" data-testid={`provider-card-${p.provider_id}`}>
                <CardContent className="pt-6 space-y-2">
                  <h3 className="font-semibold text-base">{p.name}</h3>
                  {col('physical_address') && p.physical_address && (
                    <p className="text-sm text-muted-foreground flex items-start gap-1.5">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /> {p.physical_address}
                    </p>
                  )}
                  {col('location') && (p.town || p.province) && (
                    <Badge variant="outline" className="text-xs">{[p.town, p.province].filter(Boolean).join(', ')}</Badge>
                  )}
                  {col('phone') && p.phone && (
                    <p className="text-sm flex items-center gap-1.5"><Phone className="w-4 h-4" /> {p.phone}</p>
                  )}
                  {col('email') && p.email && (
                    <p className="text-sm flex items-center gap-1.5"><Mail className="w-4 h-4" /> {p.email}</p>
                  )}
                  <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => { setSelected(p); trackEvent(USAGE_EVENTS.VIEW_PROVIDER_DETAIL, { provider_id: p.provider_id }); }} data-testid={`provider-view-${p.provider_id}`}>
                    <Eye className="w-4 h-4 mr-1" /> View Details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Page {page} of {totalPages} · {total} providers</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="provider-prev-page">
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="provider-next-page">
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="provider-details-dialog">
          <DialogHeader><DialogTitle>{selected?.name}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              {col('location') && <DetailRow label="Province" value={selected.province} />}
              {col('location') && <DetailRow label="Town" value={selected.town} />}
              {col('physical_address') && <DetailRow label="Physical Address" value={selected.physical_address} />}
              {col('postal_address') && <DetailRow label="Postal Address" value={selected.postal_address} />}
              {col('email') && <DetailRow label="Email" value={selected.email} />}
              {col('phone') && <DetailRow label="Phone" value={selected.phone} />}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MemberLayout>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b pb-2">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right">{value || '—'}</span>
  </div>
);

export default MemberProviders;
